import {StatisticFilterDef} from '../types';
import {formatDateForDisplay, getUserLocale} from './timestampUtils';
import {decodeJWT} from './jwt';
import {storage} from './localStorage';
import {isDateToken, resolveDateToken} from './statisticDateTokens';

export const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const DATETIME_RE = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/;

export const CURRENT_USER_LABEL = 'Aktuálny používateľ';

/**
 * A user filter's default meaning whoever opens the statistic. The api resolves
 * the same token (StatisticQueryService::CURRENT_USER).
 */
export const CURRENT_USER_TOKEN = '@current_user';

/** Id of the signed-in user, as user filters expect it. */
export const currentUserId = (): string | null => {
    const token = storage.getItem('token');
    const id = token ? decodeJWT(token)?.user?.id : null;
    return id ? String(id) : null;
};

/**
 * Filter values a statistic starts with: declared defaults, and user filters
 * defaulting to the signed-in user ("Moje ..." statistics would otherwise 422).
 */
export const initialStatisticFilterValues = (filters: StatisticFilterDef[]): Record<string, string> => {
    const values: Record<string, string> = {};
    filters.forEach((filter) => {
        if (filter.defaultValue) {
            // Relative dates resolve on every visit, so the input shows the concrete day
            if (filter.type === 'date' && isDateToken(filter.defaultValue)) {
                values[filter.code] = resolveDateToken(filter.defaultValue) ?? '';
            } else if (filter.type === 'user' && filter.defaultValue === CURRENT_USER_TOKEN) {
                values[filter.code] = currentUserId() ?? '';
            } else {
                values[filter.code] = filter.defaultValue;
            }
        } else if (filter.type === 'user') {
            const userId = currentUserId();
            if (userId) {
                values[filter.code] = userId;
            }
        }
    });
    return values;
};

/**
 * Table cell text. Raw SQL dates/timestamps render in the viewer's locale — the
 * AI returns raw columns instead of baking a format into the SQL. The space after
 * the dot in CLDR's Slovak pattern ("16. 03. 2026") is stripped to match the
 * app's compact date inputs (16.03.2026).
 */
export const formatStatisticCell = (value: any): string => {
    if (value === null || value === undefined) {
        return '';
    }
    if (typeof value === 'boolean') {
        return value ? 'Áno' : 'Nie';
    }
    if (typeof value === 'string') {
        const datetime = value.match(DATETIME_RE);
        if (datetime) {
            const [, y, mo, d, h, mi] = datetime;
            const date = new Date(+y, +mo - 1, +d, +h, +mi);
            if (!isNaN(date.getTime())) {
                return new Intl.DateTimeFormat(getUserLocale(), {
                    year: 'numeric', month: '2-digit', day: '2-digit',
                    hour: '2-digit', minute: '2-digit',
                }).format(date).replace(/\.\s/g, '.');
            }
        }
        if (DATE_RE.test(value)) {
            return formatDateForDisplay(value).replace(/\.\s/g, '.');
        }
    }
    return String(value);
};

/** Postgres numerics arrive as strings; anything non-numeric becomes null. */
export const toNumber = (value: any): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const number = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(number) ? number : null;
};

export const formatNumber = (value: number | null, compact = false): string => {
    if (value === null) return '–';
    return new Intl.NumberFormat(getUserLocale(), compact
        ? {notation: 'compact', maximumFractionDigits: 1}
        : {maximumFractionDigits: 2}).format(value);
};

/** Short category label for chart axes: dates as "po 21. 9.", the rest as text. */
export const formatAxisLabel = (value: any): string => {
    if (typeof value === 'string' && DATE_RE.test(value)) {
        const [y, m, d] = value.split('-').map(Number);
        return new Intl.DateTimeFormat(getUserLocale(), {weekday: 'short', day: 'numeric', month: 'numeric'})
            .format(new Date(y, m - 1, d));
    }
    return formatStatisticCell(value);
};
